import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEvent,
  KeyboardEvent,
  InputHTMLAttributes,
  ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
  useMemo,
  MouseEvent,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { useOptionallyControlledState } from "../../internal/hooks";
import { useTrackingId } from "../../hooks";
import { TextField, TextFieldProps } from "../TextField/internal/TextField";
import { childrenToString } from "../../internal/functions";
import {
  applyMask,
  getLastFilledCharacterIndex,
  getFirstFilledCharacterIndex,
  getFirstNonFixedCharacterIndex,
  getNextOpenCharacterIndex,
} from "./internal/utils";
import { MASK_CHARS } from "./internal/constants";
import { DataTrackingId } from "../../types";

/**
 * Props for InputMask when using with children
 */
interface InputMaskPropsWithChildren {
  /**
   * Custom input element to be wrapped with mask functionality.
   */
  children: ReactElement<InputHTMLAttributes<HTMLInputElement>>;
  /**
   * Additional props to pass to the input element.
   */
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

/**
 * Props for InputMask when using without children
 */
interface InputMaskPropsWithoutChildren {
  /**
   * No children allowed when using default TextField.
   */
  children?: never;
  /**
   * Props to pass to the default TextField component.
   */
  inputProps?: TextFieldProps;
}

/**
 * Configuration for custom mask handler matching
 */
interface ICustomMaskHandlerMatch {
  /**
   * Regular expression pattern to match against input.
   */
  matchPattern: RegExp;
  /**
   * Starting position in the input string.
   */
  startPosition: number;
  /**
   * Ending position in the input string.
   */
  endPosition: number;
}

/**
 * Configuration for custom mask handler application
 */
interface ICustomMaskHandlerApply {
  /**
   * Pattern to apply when match is found.
   */
  applyPattern: string;
  /**
   * Offset to apply to cursor position after transformation.
   */
  caretOffset: number;
}

/**
 * Custom mask handler for advanced input transformations
 */
interface ICustomMaskHandler {
  /**
   * Matching configuration for the handler.
   */
  match: ICustomMaskHandlerMatch;
  /**
   * Application configuration for the handler.
   */
  apply: ICustomMaskHandlerApply;
}

/**
 * Shared props for InputMask component
 */
interface SharedInputMaskProps {
  /**
   * The mask pattern to apply to the input.
   * - "9" for digits only
   * - "a" for letters only
   * - "*" for alphanumeric
   * - Any other character is treated as a fixed character
   */
  mask: string;
  /**
   * Character to display for unfilled mask positions.
   * @default "_"
   */
  defaultMaskCharacter?: string;
  /**
   * Controlled value for the input.
   */
  value?: string;
  /**
   * Default value for uncontrolled input.
   */
  defaultValue?: string;
  /**
   * Callback function called when input value changes.
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * Array of custom mask handlers for advanced transformations.
   */
  customMaskHandler?: ICustomMaskHandler[];
}

type SpecialChange = {
  reason: "Backspace" | "Delete" | "Edit";
  start: number;
  end: number;
};

/**
 * Props for the InputMask component
 * @property {string} mask - The mask pattern to apply to the input (9 for digits, a for letters, * for alphanumeric)
 * @property {string} [defaultMaskCharacter] - Character to display for unfilled mask positions
 * @property {array} [customMaskHandler] - Array of custom mask handlers for advanced transformations
 * @property {object} [inputProps] - Props to pass to the input component
 * @property {ReactElement} [children] - Custom input element to be wrapped with mask functionality
 * @property {string} [data-tracking-id] - Custom tracking ID for analytics
 */
export type InputMaskProps = SharedInputMaskProps &
  (InputMaskPropsWithChildren | InputMaskPropsWithoutChildren) &
  DataTrackingId;

/**
 * InputMask component for applying input masks and formatting to text inputs.
 *
 * @deprecated Use the TextField component with custom masking instead. Over time, we intend to release additional components with built-in masking.
 */
export const InputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (
    {
      children,
      mask,
      defaultMaskCharacter = "_",
      value,
      defaultValue,
      onChange,
      inputProps,
      "data-tracking-id": dataTrackingId,
    },
    ref,
  ) => {
    const [v, setV] = useOptionallyControlledState({
      controlledValue:
        value !== undefined
          ? applyMask(value, mask, defaultMaskCharacter)
          : undefined,
      defaultValue: applyMask(defaultValue || "", mask, defaultMaskCharacter),
      // Omitting the onChange handler here because we want to handle it ourselves (i.e. send an event instead of just the value)
    });
    const specialChange = useRef<SpecialChange | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const passInRef = useMergeRefs([inputRef, ref]);

    const currentOpenCharacterCount = useMemo(() => {
      return v.split("").filter((char) => char === defaultMaskCharacter).length;
    }, [v, defaultMaskCharacter]);

    const fixedCharacterIndices = useMemo(() => {
      const fixedIndices: number[] = [];
      mask.split("").forEach((char, i) => {
        if (!MASK_CHARS.includes(char)) {
          fixedIndices.push(i);
        }
      });
      return fixedIndices;
    }, [mask]);

    const moveCursorTo = (start: number, end: number = start) => {
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(start, end);
      });
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      let newVal = applyMask(e.target.value, mask, defaultMaskCharacter);
      if (
        // A single character was backspaced
        specialChange.current?.reason === "Backspace" &&
        specialChange.current.start === specialChange.current.end
      ) {
        // If the character before the backspace is a fixed character, we need to remove the filled character before the fixed character
        if (fixedCharacterIndices.includes(specialChange.current.start - 1)) {
          const removeIndex = getLastFilledCharacterIndex(
            newVal.slice(0, specialChange.current.start),
            mask,
          );
          newVal = applyMask(
            newVal.slice(0, removeIndex) + newVal.slice(removeIndex + 1),
            mask,
            defaultMaskCharacter,
          );
          moveCursorTo(removeIndex, removeIndex);
        } else {
          const idx = Math.min(
            getLastFilledCharacterIndex(newVal, mask) + 1,
            specialChange.current.start - 1,
          );
          moveCursorTo(idx, idx);
        }
      } else if (
        // A single character was deleted
        specialChange.current?.reason === "Delete" &&
        specialChange.current.start === specialChange.current.end
      ) {
        // If the character after the delete is a fixed character, we need to remove the filled character after the fixed character
        if (fixedCharacterIndices.includes(specialChange.current.start)) {
          const removeIndex = getFirstFilledCharacterIndex(
            newVal.slice(specialChange.current.start + 1),
            mask,
          );
          newVal = applyMask(
            newVal.slice(0, removeIndex) + newVal.slice(removeIndex + 1),
            mask,
            defaultMaskCharacter,
          );
        }
        const firstFilledIdx = getFirstFilledCharacterIndex(newVal, mask);
        const targetIdx = Math.max(firstFilledIdx, specialChange.current.start);
        moveCursorTo(targetIdx, targetIdx);
      } else if (
        specialChange.current?.reason === "Edit" &&
        specialChange.current.start === specialChange.current.end // A single character was edited
      ) {
        const firstNonFixedIdx = getFirstNonFixedCharacterIndex(
          specialChange.current.start + 1,
          fixedCharacterIndices,
        );
        moveCursorTo(firstNonFixedIdx, firstNonFixedIdx);
      } else {
        const newIndex = getNextOpenCharacterIndex(
          newVal,
          defaultMaskCharacter,
        );
        moveCursorTo(newIndex, newIndex);
      }

      setV(newVal);
      if (inputRef.current) {
        inputRef.current.value = newVal;
      }
      onChange?.({ ...e, target: { ...e.target, value: newVal } });
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      requestAnimationFrame(() => {
        if (
          e.target.selectionStart === null ||
          e.target.selectionStart === undefined ||
          e.target.selectionStart >= v.length
        ) {
          const newIndex = getNextOpenCharacterIndex(v, defaultMaskCharacter);
          moveCursorTo(newIndex, newIndex);
        }
      });
      inputProps?.onFocus?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.selectionStart === null || target.selectionEnd === null) {
        return;
      }
      if (e.key === "Backspace") {
        specialChange.current = {
          reason: "Backspace",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else if (e.key === "Delete") {
        specialChange.current = {
          reason: "Delete",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else if (currentOpenCharacterCount === 0) {
        specialChange.current = {
          reason: "Edit",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else {
        specialChange.current = null;
      }
      inputProps?.onKeyDown?.(e);
    };

    const handleClick = (e: MouseEvent<HTMLInputElement>) => {
      // For future use.
      e.preventDefault();
      e.stopPropagation();
      inputProps?.onClick?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      // For future use.
      e.preventDefault();
      e.stopPropagation();
      inputProps?.onBlur?.(e);
    };

    const mergedInputProps = {
      ...inputProps,
      ...(children && isValidElement(children) ? children.props : {}),
      value: v,
      onChange: handleChange,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      onBlur: handleBlur,
      ref: passInRef,
    };

    const isInputEmpty = useMemo(() => {
      if (v.length === 0) {
        return true;
      }
      return v === applyMask("", mask, defaultMaskCharacter);
    }, [v, defaultMaskCharacter, mask]);

    const data = {
      label:
        "label" in mergedInputProps
          ? childrenToString(mergedInputProps.label)
          : null,
      labelProps:
        "labelProps" in mergedInputProps ? mergedInputProps.labelProps : null,
      prefix: childrenToString(mergedInputProps.prefix),
      hint:
        "hint" in mergedInputProps
          ? childrenToString(mergedInputProps.hint)
          : null,
      description:
        "description" in mergedInputProps
          ? childrenToString(mergedInputProps.description)
          : null,
      size: mergedInputProps.size,
      type: mergedInputProps.type,
    };

    const trackingId = useTrackingId({
      name: "InputMask",
      data,
      hasOverride: !!dataTrackingId,
    });

    return children && isValidElement(children) && children.type === "input" ? (
      cloneElement(children, {
        ...mergedInputProps,
        "data-tracking-id": trackingId,
      } as InputHTMLAttributes<HTMLInputElement> & {
        "data-tracking-id"?: string;
      })
    ) : (
      <TextField
        data-input-empty={isInputEmpty}
        data-tracking-id={trackingId}
        {...(mergedInputProps as TextFieldProps)}
      />
    );
  },
);

InputMask.displayName = "InputMask";
